import { useEffect, useMemo, useState } from 'react';
import { Button, CustomSelect, Modal } from '@/components';
import { EMPTY_COMPANY_PREFERENCES } from '../types';
import {
  COUNTRY_OPTIONS,
  withCurrentOption,
} from '../utils/companyPreferenceOptions';
import { isBlank } from '../utils/companyPreferences';
import { CurrencySelect } from './CurrencySelect';

export function CompanyPreferencesSetupModal({
  isOpen,
  preferences,
  isUpdating = false,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY_COMPANY_PREFERENCES);
  const [showErrors, setShowErrors] = useState(false);

  const savedCountry = preferences?.country || '';
  const savedCurrency = preferences?.currency || '';
  const savedLanguage = preferences?.language || '';

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      country: savedCountry,
      currency: savedCurrency,
      language: savedLanguage,
    });
    setShowErrors(false);
  }, [isOpen, savedCountry, savedCurrency, savedLanguage]);

  const countryOptions = useMemo(
    () => withCurrentOption(COUNTRY_OPTIONS, form.country),
    [form.country],
  );

  const fieldErrors = {
    country: isBlank(form.country) ? 'Country is required' : '',
    currency: isBlank(form.currency) ? 'Currency is required' : '',
  };
  const isValid = !fieldErrors.country && !fieldErrors.currency;

  const handleSave = () => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    if (isUpdating) return;
    onSave({
      country: form.country,
      currency: form.currency,
      language: preferences?.language || '',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      showClose={false}
      size="md"
      title="Set up preferences"
      description="Select your country and currency to continue."
      footer={
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isUpdating || !isValid}
          className="px-5 py-2.5 h-[40px] text-[14px] font-medium rounded-lg"
        >
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <span className="text-base text-text-muted block mb-1">Country</span>
          <CustomSelect
            value={form.country}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, country: value }))
            }
            options={countryOptions}
            placeholder="Select country"
            disabled={isUpdating}
            searchable
            searchPlaceholder="Search country..."
            error={showErrors && Boolean(fieldErrors.country)}
            className="w-full h-[40px] sm:h-[44px]"
          />
          {showErrors && fieldErrors.country ? (
            <span className="text-status-error text-sm font-medium mt-1 block">
              {fieldErrors.country}
            </span>
          ) : null}
        </div>

        <div>
          <span className="text-base text-text-muted block mb-1">Currency</span>
          <CurrencySelect
            value={form.currency}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, currency: value }))
            }
            disabled={isUpdating}
            error={showErrors && Boolean(fieldErrors.currency)}
          />
          {showErrors && fieldErrors.currency ? (
            <span className="text-status-error text-sm font-medium mt-1 block">
              {fieldErrors.currency}
            </span>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
